import { computed, Ref, ref, shallowRef, toRef } from 'vue'

import { AxiosRequestConfig } from 'axios'

import { WidgetExtended } from '@/types/dashboard'

import { WidgetDataApi } from '@/api/widgetDataApi'
import { isExternalDataWidget } from '@/helpers/widgetUtils'
import useAbortRequest from '@/composables/useAbortRequest'

export default function <
    T,
    P extends { [Key in K]: WidgetExtended },
    K extends keyof P,
> (props: Readonly<P>, key: K) {
    // Data
    const loading = ref(false)
    const dataLoaded = ref(false)
    const widgetData = shallowRef<T | undefined>(undefined)
    const widgetRef = toRef<P, keyof P>(props, key) as Ref<WidgetExtended>

    // Composables
    const { makeRequestConfig } = useAbortRequest()

    // Computed
    const isLoading = computed(() => loading.value)
    const data = computed(() => widgetData.value)

    async function fetchData (config: AxiosRequestConfig = {}) {
        loading.value = true

        try {
            const endpoint = widgetRef.value.EndPoint

            if (endpoint) {
                if (isExternalDataWidget(widgetRef.value)) {
                    widgetData.value = await WidgetDataApi.getExternalData(
                        endpoint,
                        makeRequestConfig(config)
                    )
                } else {
                    widgetData.value = await WidgetDataApi.getData(
                        endpoint,
                        makeRequestConfig(config)
                    )
                }
            }
        } catch (e) {
            console.error(e)
        } finally {
            loading.value = false

            dataLoaded.value = true
        }
    }

    return {
        isLoading,
        dataLoaded,
        data,
        widgetRef,
        fetchData,
    }
}
